import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertOctagon, Clock3, FileJson, MapPin, RefreshCw, ServerCog } from 'lucide-react';
import { getModelRequestDetail, getModelRequests } from '../lib/api';
import { formatDuration, formatNumber, formatTime } from '../lib/format';
import type { ModelHttpRequestDetail, ModelHttpRequestSummary } from '../types';
import { ModelRequestDetailView } from './ModelRequestDetailView';
import { StatusPill } from './StatusPill';

export function ModelRequestsPanel() {
  const [items, setItems] = useState<readonly ModelHttpRequestSummary[]>([]);
  const [detail, setDetail] = useState<ModelHttpRequestDetail>();
  const [selectedId, setSelectedId] = useState<number>();
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [detailError, setDetailError] = useState<string>();
  const [roomFilter, setRoomFilter] = useState('all');

  const roomOptions = useMemo(() => [...new Set(items.flatMap((item) => item.roomId ? [item.roomId] : []))], [items]);
  const visibleItems = useMemo(() => items.filter((item) => roomFilter === 'all'
    || (roomFilter === 'unattributed' ? item.roomId === undefined : item.roomId === roomFilter)), [items, roomFilter]);
  const roomGroups = useMemo(() => {
    const groups = new Map<string, ModelHttpRequestSummary[]>();
    visibleItems.forEach((item) => {
      const key = item.roomId ?? 'unattributed';
      groups.set(key, [...(groups.get(key) ?? []), item]);
    });
    return [...groups.entries()];
  }, [visibleItems]);

  const selectRequest = useCallback(async (item: ModelHttpRequestSummary) => {
    setSelectedId(item.id);
    setDetailLoading(true);
    setDetailError(undefined);
    try {
      setDetail(await getModelRequestDetail(item.id));
    } catch (nextError) {
      setDetail(undefined);
      setDetailError(nextError instanceof Error ? nextError.message : '请求包体读取失败');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await getModelRequests();
      setItems(response.items);
      const first = response.items[0];
      if (first) await selectRequest(first);
      else {
        setSelectedId(undefined);
        setDetail(undefined);
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : '无法读取模型请求');
    } finally {
      setLoading(false);
    }
  }, [selectRequest]);

  useEffect(() => { void load(); }, [load]);

  const changeRoom = (roomId: string) => {
    setRoomFilter(roomId);
    const first = items.find((item) => roomId === 'all'
      || (roomId === 'unattributed' ? item.roomId === undefined : item.roomId === roomId));
    if (first) void selectRequest(first);
  };

  return (
    <>
      <div className="model-request-notice">
        <ServerCog size={17} />
        <div><strong>实时内存数据</strong><span>AI Backend → LLM 网关；默认最近 64 条，服务重启后清空。凭证字段由 GM API 脱敏。</span></div>
        <button className="button button--secondary" onClick={() => void load()} disabled={loading}><RefreshCw className={loading ? 'spin' : ''} size={14} />刷新</button>
      </div>
      {error && <div className="banner-error" role="alert"><AlertOctagon size={16} /><span>{error}</span></div>}
      <div className="workspace model-request-workspace">
        <section className="panel trace-list model-request-list" aria-label="模型 HTTP 请求列表">
          <header className="panel__header trace-list__header"><div><span className="eyebrow">LIVE OPS RING</span><h2>模型 HTTP 请求</h2></div><span className="count-badge">{visibleItems.length}</span></header>
          <div className="model-request-room-filter"><label><MapPin size={13} />Room</label><select aria-label="按 Room ID 筛选" value={roomFilter} onChange={(event) => changeRoom(event.target.value)}><option value="all">全部 Room</option>{roomOptions.map((roomId) => <option value={roomId} key={roomId}>{roomId}</option>)}<option value="unattributed">未关联 Room</option></select></div>
          <div className="trace-list__items">
            {roomGroups.map(([roomId, roomItems]) => <section className="room-group" aria-label={`Room ${roomId}`} key={roomId}>
              <header className="room-group__header"><span><MapPin size={11} />{roomId === 'unattributed' ? '未关联 Room' : roomId}</span><b>{roomItems.length}</b></header>
              {roomItems.map((item) => (
              <button className={`trace-row${selectedId === item.id ? ' trace-row--selected' : ''}`} onClick={() => void selectRequest(item)} key={item.id}>
                <i className={`trace-row__rail trace-row__rail--${item.status}`} />
                <span className="trace-row__body">
                  <span className="trace-row__topline"><strong>Request #{item.id}</strong><StatusPill status={item.status} compact /></span>
                  <span className="trace-row__ids"><code>{item.runId ?? 'unattributed'}</code><code>{item.kind ?? 'unknown'}</code></span>
                  <span className="trace-row__room"><MapPin size={10} />{item.roomId ?? '未关联 Room'}</span>
                  <span className="trace-row__meta"><span><Clock3 size={11} />{formatTime(item.observedAt)}</span><span>{formatNumber(item.requestBytes)} B</span></span>
                  <span className="trace-row__metrics"><b>{formatDuration(item.durationMs)}</b><b>{formatNumber(item.tokenUsage.total)} tokens</b></span>
                </span>
              </button>
              ))}
            </section>)}
            {!loading && visibleItems.length === 0 && <div className="empty-state"><FileJson size={25} /><strong>暂无请求</strong><p>当前 Room 没有可关联的模型调用。</p></div>}
          </div>
        </section>
        <section className="panel model-request-detail" aria-label="模型 HTTP 请求详情">
          {detailLoading ? <div className="empty-state"><RefreshCw className="spin" size={24} /><strong>读取请求包体…</strong></div>
            : detailError ? <div className="empty-state empty-state--error"><AlertOctagon size={25} /><strong>包体读取失败</strong><p>{detailError}</p></div>
              : detail ? <ModelRequestDetailView detail={detail} />
                : <div className="empty-state"><FileJson size={25} /><strong>选择一条请求</strong><p>查看脱敏后的 HTTP 包体和响应。</p></div>}
        </section>
      </div>
    </>
  );
}
